<template>
    <div class="mt-10 rounded-md bg-white p-6 shadow-md">
        <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="flex flex-col sm:flex-row sm:items-center">
                <div
                    class="h-20 w-20 shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                >
                    <img
                        :src="
                            selectedTeam.profile_photo_path
                                ? selectedTeam.profile_photo_path
                                : selectedTeam.profile_photo_url
                        "
                        class="h-20 w-20 rounded-full bg-white object-cover"
                    />
                </div>
                <div class="mt-2 flex flex-col sm:ml-10 sm:mt-0">
                    <h2 class="text-2xl font-bold text-gray-800">
                        {{ selectedTeam.name }}
                    </h2>
                    <div class="text-xl font-medium text-gray-500">
                        {{ selectedTeam.email }}
                    </div>
                </div>
            </div>

            <div>
                <div class="flex flex-wrap items-center gap-4">
                    <AtomicButton
                        v-if="
                            selectedTeam.proSubscription ||
                            selectedTeam.pluginSubscription
                        "
                        color="yellow"
                        size="sm"
                        @click="showManageRecurringPaymentRequestsModal = true"
                    >
                        Manage payment request recurring
                    </AtomicButton>

                    <AtomicButton
                        color="yellow"
                        size="sm"
                        @click="showPaymentRequestModal = true"
                    >
                        <template #icon>
                            <BanknotesIcon class="mr-2 h-6 w-6" />
                        </template>

                        Payment request
                    </AtomicButton>

                    <AtomicButton
                        size="lg"
                        color="yellow"
                        class="mt-5 sm:mt-0"
                        @click="transferToTeamModal = true"
                    >
                        <template #icon>
                            <ChevronDoubleRightIcon class="mr-2 h-6 w-6" />
                        </template>
                        Transfer to team
                    </AtomicButton>

                    <AtomicButton
                        v-if="!selectedTeam.new_flexibee_id"
                        size="sm"
                        color="yellow"
                        class="ml-2 mt-5 sm:mt-0"
                        @click="createFlexibeeId(selectedTeam)"
                    >
                        <template #icon>
                            <DocumentTextIcon class="mr-2 h-6 w-6" />
                        </template>
                        Create Flexibee ID
                    </AtomicButton>

                    <AtomicButton
                        size="lg"
                        color="red"
                        class="ml-2 mt-5 sm:mt-0"
                        @click="handleDeleteTeam(selectedTeam)"
                    >
                        <template #icon>
                            <TrashIcon class="mr-2 h-6 w-6" />
                        </template>
                        Delete team
                    </AtomicButton>
                </div>
            </div>
        </div>
        <div class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="flex flex-col">
                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">ID:</div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedTeam.id }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Flexibee ID:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{
                            selectedTeam.new_flexibee_id
                                ? 'TOP_' + selectedTeam.id
                                : '-'
                        }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        Created:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{
                            format(
                                new Date(selectedTeam.created_at),
                                "d. L. yyyy '('HH:mm:ss')'"
                            )
                        }}
                    </div>
                </div>

                <div class="mt-1 text-sm text-gray-500">
                    <div class="font-medium text-gray-800">Subscription:</div>

                    <div v-if="selectedTeam.proSubscription">
                        PRO
                        <span>
                            ({{
                                selectedTeam.proSubscription
                                    .subscription_plan_uuid
                            }})</span
                        >

                        <span
                            v-if="
                                selectedTeam.proSubscription
                                    .recurring_payment_request
                            "
                        >
                            | recurring payment requests</span
                        >
                    </div>
                    <div v-if="selectedTeam.pluginSubscription">
                        Plugin
                        <span>
                            ({{
                                selectedTeam.pluginSubscription
                                    .subscription_plan_uuid
                            }})</span
                        >

                        <span
                            v-if="
                                selectedTeam.pluginSubscription
                                    .recurring_payment_request
                            "
                        >
                            | recurring payment requests</span
                        >
                    </div>
                    <div v-if="selectedTeam.hasLegacySubscription">Legacy</div>
                </div>
            </div>

            <div class="flex flex-col">
                <div class="text-gray-80 text-sm font-medium">
                    Team trial ends:

                    <TextInput
                        v-model="teamTrialDateComputed"
                        type="datetime-local"
                        class="mt-3 w-48"
                    />
                    <AtomicButton
                        size="sm"
                        color="purple"
                        class="mt-2"
                        @click="handleExtendTrial"
                    >
                        Set/Extend Team Trial
                    </AtomicButton>
                </div>
            </div>

            <div class="flex flex-col">
                <div class="text-sm font-medium text-gray-800">
                    Billing information:
                </div>

                <div
                    v-if="
                       billingAddresses.length === 0
                    "
                    class="mt-2 text-sm text-gray-500"
                >
                    No billing addresses found
                </div>

                <UserTabsProfileBillIngInfo
                    v-else
                    :billing-addresses="billingAddresses"
                />
            </div>
        </div>
    </div>

    <TransferToTeam
        v-if="transferToTeamModal"
        :team="selectedTeam"
        @close="transferToTeamModal = false"
    />

    <MakePaymentRequestModal
        v-if="showPaymentRequestModal"
        :team="selectedTeam"
        @close="showPaymentRequestModal = false"
    ></MakePaymentRequestModal>

    <ManageRecurringPaymentRequestsModal
        v-if="
            showManageRecurringPaymentRequestsModal &&
            (selectedTeam.proSubscription || selectedTeam.pluginSubscription)
        "
        :pro="(selectedTeam.proSubscription as ISubscription | undefined)"
        :plugin="(selectedTeam.pluginSubscription as ISubscription | undefined)"
        @close="showManageRecurringPaymentRequestsModal = false"
        @enable="enablePaymentRequestRecurring"
        @disable="disablePaymentRequestRecurring"
    ></ManageRecurringPaymentRequestsModal>
</template>

<script lang="ts" setup>
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import { format } from 'date-fns';
import {
    TrashIcon,
    ChevronDoubleRightIcon,
    DocumentTextIcon,
} from '@heroicons/vue/24/outline';

import { route } from 'ziggy-js';
import { router } from '@inertiajs/vue3';
import useConfirm from '@/composables/useConfirm';
import { create } from '@/composables/useNotifications';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import axios from 'axios';
import TransferToTeam from '@/Pages/Kiosk/Teams/components/TransferToTeam.vue';
import ISubscription from '@/types/Subscription/ISubscription';
import IBillingAddress from '@/types/BillingAddresses/IBillingAddress';

const props = defineProps<{
    selectedTeam: ICurrentTeam;
    billingAddresses: IBillingAddress[];
}>();

const { show } = useConfirm();

const teamTrialDate = ref('');
const transferToTeamModal = ref(false);
const showManageRecurringPaymentRequestsModal = ref(false);
const showPaymentRequestModal = ref(false);

const handleDeleteTeam = async (selectedTeam: ICurrentTeam) => {
    const confirmed = await show({
        header: `Delete team ${selectedTeam.name}`,
        message: `Are you sure you want to delete team ${selectedTeam.name}?`,
        type: 'danger',
        confirm: 'Delete team',
    });
    if (confirmed) {
        await deleteTeam(selectedTeam);
    }
};

const deleteTeam = async (selectedTeam: ICurrentTeam) => {
    try {
        const res = await axios.delete(
            route('kiosk.teams.delete', { team: selectedTeam.id })
        );
        if (res.data.success) {
            create({
                title: `Team deleted`,
                type: 'success',
            });
            router.get(route('kiosk.teams.index'));
        } else {
            create({
                title: 'Team not deleted',
                text: `There was an error deleting team.`,
                type: 'error',
            });
        }
    } catch (err) {
        create({
            title: 'Team not deleted',
            text: `There was an error deleting team.`,
            type: 'error',
        });
    }
};

const createFlexibeeId = async (team: ICurrentTeam) => {
    const confirmed = await show({
        header: `Create Flexibee ID`,
        message: `Are you sure you want to create Flexibee ID for this team?`,
        type: 'info',
        confirm: 'Create',
    });

    if (confirmed) {
        const response = await axios.post(
            route('kiosk.teams.flexibee.store', {
                team: team.id,
            })
        );

        if (response.status === 200) {
            create({
                title: `Flexibee ID created`,
                type: 'success',
            });
            team.new_flexibee_id = response.data.data.new_flexibee_id;
        } else {
            create({
                title: 'Flexibee ID not created',
                text: `There was an error creating Flexibee ID.`,
                type: 'error',
            });
        }
    }
};

const handleExtendTrial = async () => {
    const confirmed = await show({
        header: `Extend team trial`,
        message: `Are you sure you want to update trial date of team?`,
        type: 'info',
        confirm: 'Update',
    });

    if (confirmed) {
        router.put(
            route('kiosk.teams.trial-extend.update', {
                team: props.selectedTeam.id,
            }),
            {
                date: teamTrialDate.value,
            },
            {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    create({
                        title: `Trial extended`,
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Trial not extended',
                        text: `There was an error extending trial.`,
                        type: 'error',
                    });
                },
                onFinish: () => {
                    teamTrialDate.value = '';
                },
            }
        );
    }
};

const teamTrialDateComputed = computed({
    get: function () {
        return props.selectedTeam.trial_ends_at
            ? new Date(props.selectedTeam.trial_ends_at)
                  .toISOString()
                  .substring(0, 19)
            : '';
    },
    set: function (newValue: string) {
        teamTrialDate.value = newValue;
    },
});

const enablePaymentRequestRecurring = async (subscriptionId: number) => {
    const confirmed = await show({
        header: 'Enable recurring payment requests',
        message:
            'Are you sure you want to enable recurring payment requests for this subscription?',
        type: 'danger',
        confirm: 'Enable',
    });

    if (confirmed) {
        showManageRecurringPaymentRequestsModal.value = false;

        router.post(
            route(
                'kiosk.subscriptions.enable-recurring-payment-requests.store',
                {
                    subscription: subscriptionId,
                }
            ),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Payment request recurring enabled',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Payment request recurring not enabled',
                        text: 'There was an error enabling payment request recurring.',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const disablePaymentRequestRecurring = async (subscriptionId: number) => {
    const confirmed = await show({
        header: 'Disable recurring payment requests',
        message:
            'Are you sure you want to disable recurring payment requests for this subscription? It will also CANCEL it.',
        type: 'danger',
        confirm: 'Disable',
    });

    if (confirmed) {
        showManageRecurringPaymentRequestsModal.value = false;

        router.post(
            route(
                'kiosk.subscriptions.disable-recurring-payment-requests.store',
                {
                    subscription: subscriptionId,
                }
            ),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Payment request recurring disabled',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Payment request recurring not disabled',
                        text: 'There was an error disabling payment request recurring.',
                        type: 'error',
                    });
                },
            }
        );
    }
};
</script>
